import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { ButtonGroupNew, IconButtonDropdownNew, Text, useIsMobile } from '@lune-fe/lune-ui-lib'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import CloudDownloadIcon from '@mui/icons-material/CloudDownloadOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined'
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import { Box, Stack } from '@mui/material'
import fileDownload from 'js-file-download'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Range } from 'react-date-range'
import { useNavigate } from 'react-router-dom'

import { formatNumbers } from '../../utils'
import {
    cockpitEmissionsChartColors,
    cockpitEmissionsChartLabels,
    cockpitLogisticsChartOrdering,
    CockpitLogisticsEmissionsCategories,
    downloadImageFromHtml,
    emissionsChartColors,
    emissionsChartLabels,
    IEmissionsTimeseries,
    IWtwBreakdownTimeseries,
    logisticsChartOrdering,
    LogisticsEmissionsCategories,
    wtwBreakdownChartColors,
    wtwBreakdownChartLabels,
    wtwBreakdownChartOrdering,
    WtwStage,
} from '../charts/chartUtils'
import EmissionsChartSection from '../charts/EmissionsChartSection'

import TrendIndicator from './TrendIndicator'

export const EmissionBreakdownChartToggleNew: FC<{
    /** Chart heading. Defaults to the translated "Your shipments have emitted" when omitted. */
    title?: string
    /**
     * Show the emissions total below the heading. Defaults to true.
     * When false, hide the total and give the heading stronger emphasis.
     */
    showTotal?: boolean
    data?: {
        emissionTimeseries: (IEmissionsTimeseries & { parcel?: number })[]
        wtwBreakdownTimeseries: IWtwBreakdownTimeseries[]
    }
    /**
     * Include the 'parcel' category (shipments whose mode of transport is unknown) in the
     * "By category" chart, legend and total. Only cockpit data has a 'parcel' field.
     */
    includeParcel?: boolean
    dateRange: Range
    loading: boolean
    emissionsListProps?: {
        organisationId: string
        type: string
        accountHandle: string
        accessToken?: string
    }
    accountId?: string
    /**
     * Signed percentage change compared with the previous period.
     * Positive values render with an upward arrow, negative values with a downward arrow.
     * When undefined, the trend indicator and "vs previous period" label are hidden.
     * The indicator uses neutral styling unless trendIndicatorColorIndicator is enabled.
     */
    trendIndicator?: number
    trendIndicatorColorIndicator?: boolean
    csvDownloadFn?: (
        organisationId: string,
        type: string,
        accountHandle: string,
        from: string,
        through: string,
        accessToken?: string,
    ) => Promise<string>
    reducedSpace?: boolean
    // eslint-disable-next-line complexity
}> = ({
    title,
    showTotal = true,
    data,
    dateRange,
    loading,
    emissionsListProps,
    accountId,
    trendIndicator,
    trendIndicatorColorIndicator = false,
    csvDownloadFn,
    reducedSpace,
    includeParcel = false,
}) => {
    useLingui()

    const emissionTimeseries = data?.emissionTimeseries
    const wtwBreakdownTimeseries = data?.wtwBreakdownTimeseries
    const printRef = useRef<HTMLElement>(null)
    const { isMobile } = useIsMobile()
    const [total, setTotal] = useState<string>()
    const navigate = useNavigate()

    const downloadCsv = () => {
        if (csvDownloadFn && emissionsListProps) {
            csvDownloadFn(
                emissionsListProps.organisationId,
                emissionsListProps.type,
                emissionsListProps.accountHandle,
                dateRange.startDate!.toLocaleDateString('sv-SE'),
                dateRange.endDate!.toLocaleDateString('sv-SE'),
                emissionsListProps.accessToken,
            ).then((data) =>
                fileDownload(data, `logistics-emissions-${emissionsListProps.accountHandle}.csv`),
            )
        }
    }

    const downloadChart = () => {
        if (printRef.current) {
            downloadImageFromHtml(printRef.current, `emissions-graph`)
        }
    }

    const emissionsListPath: string | undefined = useMemo(() => {
        if (!emissionsListProps) {
            return undefined
        }
        const { organisationId, type, accountHandle } = emissionsListProps
        const queryParams = window.location.href.split('?')[1]
        const basePath =
            type === 'live'
                ? `/logistics/${organisationId}/${accountHandle}/emissions`
                : `/logistics/test/${organisationId}/${accountHandle}/emissions`
        return `${basePath}${queryParams ? `?${queryParams}` : ''}`
    }, [
        emissionsListProps?.organisationId,
        emissionsListProps?.type,
        emissionsListProps?.accountHandle,
        window.location.href,
    ])

    const showOffsetButton = useMemo(() => {
        if (typeof window === 'undefined') {
            return false
        }
        const params = new URLSearchParams(window.location.search)
        return params.get('show_offset') === 'true'
    }, [window.location.href])

    useEffect(() => {
        let airTotal = 0
        let seaTotal = 0
        let roadTotal = 0
        let railTotal = 0
        let inlandWaterwayTotal = 0
        let logisticsSiteTotal = 0
        let parcelTotal = 0

        if (!emissionTimeseries) {
            return
        }

        for (const item of emissionTimeseries) {
            airTotal += item.air
            seaTotal += item.sea
            roadTotal += item.road
            railTotal += item.rail
            inlandWaterwayTotal += item.inlandWaterway
            logisticsSiteTotal += item.logisticSite
            parcelTotal += item.parcel ?? 0
        }

        setTotal(
            formatNumbers(
                airTotal +
                    seaTotal +
                    roadTotal +
                    railTotal +
                    inlandWaterwayTotal +
                    logisticsSiteTotal +
                    (includeParcel ? parcelTotal : 0),
                2,
            ).toString(),
        )
    }, [emissionTimeseries, wtwBreakdownTimeseries, includeParcel])

    const byCategoryChart = includeParcel ? (
        <EmissionsChartSection
            loading={loading}
            data={(emissionTimeseries ?? []) as (IEmissionsTimeseries & { parcel: number })[]}
            dataColours={cockpitEmissionsChartColors}
            dataLabels={cockpitEmissionsChartLabels}
            dateRange={dateRange}
            showLegend={true}
            skipZeroOnTooltip={true}
            labelOrdering={(
                a: CockpitLogisticsEmissionsCategories,
                b: CockpitLogisticsEmissionsCategories,
            ) => cockpitLogisticsChartOrdering[a] - cockpitLogisticsChartOrdering[b]}
            isEmpty={Number(total) === 0 || emissionTimeseries?.length === 0}
        />
    ) : (
        <EmissionsChartSection
            loading={loading}
            data={emissionTimeseries ?? []}
            dataColours={emissionsChartColors}
            dataLabels={emissionsChartLabels}
            dateRange={dateRange}
            showLegend={true}
            skipZeroOnTooltip={true}
            labelOrdering={(a: LogisticsEmissionsCategories, b: LogisticsEmissionsCategories) =>
                logisticsChartOrdering[a] - logisticsChartOrdering[b]
            }
            isEmpty={Number(total) === 0 || emissionTimeseries?.length === 0}
        />
    )
    const byWtwChart = (
        <EmissionsChartSection
            loading={loading}
            data={wtwBreakdownTimeseries ?? []}
            dataColours={wtwBreakdownChartColors}
            dataLabels={wtwBreakdownChartLabels}
            dateRange={dateRange}
            showLegend={true}
            skipZeroOnTooltip={true}
            labelOrdering={(a: WtwStage, b: WtwStage) =>
                wtwBreakdownChartOrdering[a] - wtwBreakdownChartOrdering[b]
            }
            isEmpty={Number(total) === 0 || wtwBreakdownTimeseries?.length === 0}
        />
    )

    const [selectedChart, setSelectedChart] = useState<string>('category')
    // For estimates before this date there will not be WTW breakdown data since
    // emission factors were not introduced. Do not show WTW breakdown graph
    // Note: We are aware that shipped_at !== created_at, meaning that we might
    // be hiding some valid WTW breakdown data with this filter, even so we have
    // chosen to press onward with this change since showing empty data looks bad.
    const hideWtwStageChart = dateRange.startDate && dateRange.startDate < new Date('2024-11-08')

    return (
        <>
            <Box sx={{ mt: reducedSpace ? 0 : isMobile ? 5 : 9 }}>
                <Box
                    sx={{
                        mt: '0 !important',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        mb: isMobile ? 2 : -5,
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        {!hideWtwStageChart && (
                            <ButtonGroupNew
                                value={selectedChart}
                                onChange={(selection) => setSelectedChart(selection)}
                                items={[
                                    {
                                        value: 'category',
                                        label: t`By category`,
                                    },
                                    {
                                        value: 'wtwStage',
                                        label: t`By WTW stage`,
                                    },
                                ]}
                            />
                        )}
                        <IconButtonDropdownNew menuAutoWidth icon={<MoreVertOutlinedIcon />}>
                            {showOffsetButton && accountId && (
                                <IconButtonDropdownNew.MenuItem
                                    title={t`Offset now`}
                                    onClick={() =>
                                        navigate(
                                            `https://pay.lune.co/${accountId}?quantity=${total}`,
                                        )
                                    }
                                    icon={<CloudDownloadIcon />}
                                />
                            )}
                            {emissionsListPath && (
                                <IconButtonDropdownNew.MenuItem
                                    title={t`View individual calculations`}
                                    onClick={() => navigate(emissionsListPath)}
                                    icon={<FormatListBulletedOutlinedIcon />}
                                />
                            )}
                            {!isMobile &&
                                dateRange.startDate &&
                                dateRange.endDate &&
                                (csvDownloadFn ? (
                                    <>
                                        <IconButtonDropdownNew.MenuItem
                                            title={t`Download CSV`}
                                            onClick={() => downloadCsv()}
                                            icon={<TableChartOutlinedIcon />}
                                        />
                                        <IconButtonDropdownNew.MenuItem
                                            title={t`Download image`}
                                            onClick={() => downloadChart()}
                                            icon={<BarChartOutlinedIcon />}
                                        />
                                    </>
                                ) : (
                                    <IconButtonDropdownNew.MenuItem
                                        title={t`Download graph`}
                                        onClick={() => downloadChart()}
                                        icon={<FileDownloadOutlinedIcon />}
                                    />
                                ))}
                        </IconButtonDropdownNew>
                    </Stack>
                </Box>
                <Box ref={printRef}>
                    <Box>
                        <Text
                            variant={showTotal ? 'body2' : 'body2-medium'}
                            sx={{ mb: 1 }}
                            color={showTotal ? `TextSecondary` : `TextPrimary`}
                        >
                            {title ?? t`Your shipments have emitted`}
                        </Text>
                        {showTotal && <Text variant={'h4'}>{total} tCO₂e</Text>}
                        {trendIndicator !== undefined && (
                            <TrendIndicator
                                trendIndicator={trendIndicator}
                                colorIndicator={trendIndicatorColorIndicator}
                            />
                        )}
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        {hideWtwStageChart ? (
                            byCategoryChart
                        ) : (
                            <>{selectedChart === 'category' ? byCategoryChart : byWtwChart}</>
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    )
}

export default EmissionBreakdownChartToggleNew
